import createEmotionOriginal from '@emotion/css/create-instance';
import { serializeStyles } from '@emotion/serialize';
import { getRegisteredStyles, insertStyles } from '@emotion/utils';
import { margeProps } from '@winter-love/use';
import { isSSR } from '@winter-love/utils';
import clsx from 'clsx';
import { getCurrentInstance, inject, defineComponent, h, Fragment } from 'vue-demi';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const EMOTION_CACHE_CONTEXT = Symbol('emotion-cash');

const EMOTION_THEME_CONTEXT = Symbol('emotion-theme');
const useTheme = (theme = {}) => {
    var _a;
    const instance = getCurrentInstance();
    const props = (_a = instance === null || instance === void 0 ? void 0 : instance.props) !== null && _a !== void 0 ? _a : {};
    if (props.theme) {
        return props.theme;
    }
    return inject(EMOTION_THEME_CONTEXT, theme);
};

const toBeClassName = (value) => {
    if (typeof value === 'function') {
        return;
    }
    return value;
};
const defaultProps = {
    as: null,
    theme: null,
};
/**
 * creates new Styled function
 * @param emotion
 */
// eslint-disable-next-line max-lines-per-function
const createStyled = (emotion) => {
    function styled(element, options) {
        const { label: _label, target, name, props: stylePropsOptions = {}, stylePortal, passAs = false, } = options !== null && options !== void 0 ? options : {};
        const label = _label === true ? name : _label;
        const _target = target ? ` ${target}` : '';
        // const getProps = createGetProps(stylePropsOptions, stylePortal)
        if (process.env.NODE_ENV === 'development' && defaultProps[stylePortal] === null) {
            console.warn('stylePortal should not be as or theme');
        }
        return (...args) => {
            const _args = [...args, { label }];
            const { cache: masterCache, theme: masterTheme } = emotion;
            return defineComponent({
                computed: {
                    rootElement() {
                        return this.$refs.root;
                    },
                },
                name: name || label || 'emotion',
                props: margeProps(Object.assign(Object.assign({}, stylePropsOptions), (stylePortal ? { [stylePortal]: null } : {})), defaultProps),
                setup(props, { attrs, slots }) {
                    const theme = useTheme(masterTheme);
                    const cache = inject(EMOTION_CACHE_CONTEXT, masterCache);
                    // eslint-disable-next-line max-statements
                    return () => {
                        const { as, theme: themeInProps } = props, restProps = __rest(props, ["as", "theme"]);
                        const _restProps = stylePortal ? Object.assign(Object.assign({}, restProps), { [stylePortal]: undefined }) : restProps;
                        const styleProps = Object.assign(Object.assign({}, _restProps), (stylePortal ? props[stylePortal] : _restProps));
                        const _element = passAs ? element : (as !== null && as !== void 0 ? as : element);
                        // const theme = useTheme(masterTheme)
                        // const cache = inject(EMOTION_CACHE_CONTEXT, masterCache)
                        const isStringElement = typeof _element === 'string';
                        const classInterpolations = [];
                        const { class: classes } = attrs, restNextAttrs = __rest(attrs
                        // 만약 as 를 다음으로 넘긴다면
                        , ["class"]);
                        // 만약 as 를 다음으로 넘긴다면
                        if (passAs) {
                            restNextAttrs.as = as;
                        }
                        const allAttrs = Object.assign(Object.assign({}, styleProps), { theme });
                        const registeredClassName = getRegisteredStyles(cache.registered, classInterpolations, clsx(toBeClassName(classes)));
                        const serialized = serializeStyles([..._args, ...classInterpolations], cache.registered, allAttrs);
                        const rules = insertStyles(cache, serialized, isStringElement);
                        const className = `${registeredClassName}${cache.key}-${serialized.name}${_target}`;
                        const vNode = h(_element, Object.assign(Object.assign({}, restNextAttrs), { class: className, ref: 'root' }), slots);
                        if (isSSR() && typeof rules !== 'undefined') {
                            // eslint-disable-next-line prefer-destructuring
                            let next = serialized.next;
                            let dataEmotion = serialized.name;
                            while (typeof next !== 'undefined') {
                                dataEmotion += ` ${next.name}`;
                                // eslint-disable-next-line prefer-destructuring
                                next = next.next;
                            }
                            return (h(Fragment, [
                                h('style', { 'data-emotion': dataEmotion, nonce: cache.sheet.nonce }, rules),
                                vNode,
                            ]));
                        }
                        return vNode;
                    };
                },
            });
        };
    }
    return styled;
};

/**
 * creates emotion members & the styled function
 * @param options
 */
const createEmotion = (options = {}) => {
    const { theme, key = 'css' } = options, restOptions = __rest(options, ["theme", "key"]);
    const emotion = createEmotionOriginal(Object.assign(Object.assign({}, restOptions), { key }));
    const styled = createStyled(Object.assign(Object.assign({}, emotion), { theme }));
    return Object.assign(Object.assign({}, emotion), { install: (app, options = {}) => {
            const { theme: _theme = theme } = options;
            app.provide(EMOTION_CACHE_CONTEXT, emotion.cache);
            // provide theme if the options have it
            if (_theme) {
                app.provide(EMOTION_THEME_CONTEXT, _theme);
            }
        }, styled });
};

const createDirective = (emotion, options = {}) => {
    const { css } = emotion;
    const { theme = {}, systems = (props) => props } = options;
    const getCss = (props, theme, systemName = 'default') => {
        var _a;
        const { __system__, theme: _theme } = props, restProps = __rest(props, ["__system__", "theme"]);
        const system = (_a = __system__ !== null && __system__ !== void 0 ? __system__ : systems[systemName]) !== null && _a !== void 0 ? _a : [function defaultSystem(props) {
                // removes theme because the props become css style
                return Object.assign(Object.assign({}, props), { theme: undefined });
            }];
        const nextProps = Object.assign(Object.assign({}, restProps), { 
            // uses theme in the props or uses theme in options
            theme: _theme !== null && _theme !== void 0 ? _theme : theme });
        return css(...system.map((value) => {
            if (typeof value === 'function') {
                return value(nextProps);
            }
            return value;
        }));
    };
    const getClassName = (binding) => {
        const { value, arg } = binding;
        if (typeof value !== 'object' || Array.isArray(value)) {
            return;
        }
        return getCss(value, theme, arg);
    };
    const updateClassName = (el, binding) => {
        var _a;
        const { previousClassName } = (_a = el.__emotion__) !== null && _a !== void 0 ? _a : {};
        if (previousClassName) {
            el.classList.remove(previousClassName);
        }
        const className = getClassName(binding);
        el.__emotion__ = {
            previousClassName: className,
        };
        if (className) {
            el.classList.add(className);
        }
    };
    return {
        getSSRProps(binding) {
            return {
                class: getClassName(binding),
            };
        },
        mounted(el, binding) {
            updateClassName(el, binding);
        },
        updated(el, binding) {
            updateClassName(el, binding);
        },
    };
};

export { EMOTION_CACHE_CONTEXT, EMOTION_THEME_CONTEXT, createDirective, createEmotion, createStyled, useTheme };
